import { createFileRoute, Link } from "@tanstack/react-router";
import { canonicalLink, pathFromMatch, routeMeta } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { ServicePage } from "@/components/ServicePage";
import { SeriesBadge } from "@/components/SeriesBadge";
import hero from "@/assets/welding-fabrication-hero.jpg";
import heroPicture from "@/assets/welding-fabrication-hero.jpg?w=640;1024;1600;1920&format=avif;webp;jpeg&as=picture";
import aluminumFrame from "@/assets/welding-aluminum-frame.jpg";
import framesYard from "@/assets/welding-frames-yard.jpg";
import aluminumCanopy from "@/assets/welding-aluminum-commercial-canopy.jpg";
import aluminumCanopyFrame from "@/assets/welding-aluminum-canopy-frame.jpg";
import poolGateShade from "@/assets/welded-aluminum-pool-gate-shade.jpg";
import bleacherWeldDetail from "@/assets/red-powder-coated-welded-bracket-bleacher-detail.jpg";
import shopFrameStand from "@/assets/welding-shop-aluminum-frame-stand.jpg";
import fabricatorPortrait from "@/assets/welding-fabricator-portrait-frame.jpg";

export const Route = createFileRoute("/custom-fabrication")({
  head: (ctx) => ({
      links: [canonicalLink(pathFromMatch(ctx))],
      meta: routeMeta(ctx, {
        title: "Aluminum/Steel Welding & Custom Fabrication in San Antonio, Austin, and Texas | Texas Made Shade",
        description: "Trail Boss™ welding & custom fabrication — TIG/MIG aluminum and steel, custom frames and brackets for San Antonio, Austin and all of Texas.",
        image: hero,
        socialTitle: "Aluminum/Steel Welding & Custom Fabrication — San Antonio, Austin & Texas",
        socialDescription: "TIG/MIG aluminum and steel welding plus custom canvas — serving San Antonio, Austin and all of Texas from our Seguin, TX shop.",
        keywords: "aluminum welding San Antonio, aluminum welding Austin, steel welding Texas, custom fabrication San Antonio, custom fabrication Austin, TIG welding Texas, MIG welding Texas, aluminum welding Houston, custom aluminum frames, welded brackets, awning frame fabrication, sail shade post welding, ranch equipment welding",
      }),
    scripts: serviceHeadScripts({
      path: "/custom-fabrication",
      serviceName: "Welding & Custom Fabrication",
      breadcrumbName: "Custom Fabrication",
      description: "Aluminum and steel welding, custom frames, brackets, prototypes and full canvas builds — one-shop fabrication in Seguin, TX.",
    }),
    }),
  component: () => (
    <ServicePage
      currentPath="/custom-fabrication"
      serviceName="Custom Fabrication"
      keywordOutline={[
        { id: "overview", label: "Custom Aluminum Welding & Fabrication in Texas" },
        { id: "why-us", label: "Why Choose Texas Made Shade for Custom Fabrication" },
        { id: "details", label: "TIG/MIG Welding, Aluminum & Steel Builds" },
        { id: "applications", label: "Where We Build Custom Fabricated Frames" },
        { id: "faqs", label: "Custom Fabrication FAQs" },
      ]}
      eyebrow="★ WELDING & FABRICATION ★"
      title="Aluminum/Steel Welding & Custom Fabrication in San Antonio, Austin, and Texas"
      intro="Aluminum and steel welding plus custom fabrication serving San Antonio, Austin, Houston, New Braunfels, the Hill Country and the Texas Gulf Coast — a full TIG/MIG welding and industrial canvas shop based in Seguin, TX. We weld aluminum and steel frames, brackets, awning supports, sail shade posts and one-off custom projects daily."
      heroImage={heroPicture}
      preContent={
        <div className="flex justify-center py-8">
          <SeriesBadge slug="trail-boss" size="lg" priority />
        </div>
      }
      heroCta={{
        eyebrow: "In-House Welding & Fabrication Shop · Seguin, TX",
        headline: "Need Something Custom Built? Get a Free Estimate Today.",
        subhead: "Aluminum frames, steel structures, brackets, prototypes, or full canvas builds — send a sketch or photo and we'll come back same business day with sizing questions and a no-pressure quote.",
        primaryHref: `/#lets-talk?type=${encodeURIComponent("Aluminum Welding / Fabrication")}&notes=${encodeURIComponent("Custom welding / fabrication inquiry — please describe the project, materials (aluminum, steel, canvas), approximate dimensions, and timeline.")}`,
        primaryLabel: "Get My Free Estimate",
        phoneNumber: "+1 (830) 390-9588",
      }}
      features={[
        "TIG aluminum welding — clean structural welds for frames & canopies",
        "MIG welding for steel frames, racks and heavy-duty fabrication",
        "Custom aluminum awning frames, sail shade posts and patio cover frames",
        "Welded brackets, mounting hardware and one-off prototypes",
        "Industrial canvas sewing — Sunbrella, Tempotest, marine vinyl",
        "Powder coat, paint and raw mill finish options",
        "Serving San Antonio, Austin, Houston, New Braunfels and the Hill Country",
      ]}
      body={[
        { heading: "Aluminum Welding", text: "Lightweight, corrosion-resistant aluminum frames welded in-house. Coastal-grade for the Gulf, structural-grade for shade structures." },
        { heading: "Steel Welding & Heavy Frames", text: "When the job needs steel — sail shade posts, awning supports, ranch equipment — we weld it to spec." },
        { heading: "Custom Canvas & Sewing", text: "Full industrial sewing shop. We pattern, cut, and stitch any custom canvas project with marine-grade thread and hardware." },
      ]}
      faqs={[
        {
          q: "What kinds of aluminum welding and custom fabrication do you do in Seguin, TX?",
          a: "TIG and MIG aluminum and steel welding — custom awning frames, sail shade posts, marine hardware, ranch equipment, dog boxes, brackets, prototypes and weld repair. If you can sketch it or photograph it, our Seguin, TX shop can usually build it for San Antonio, New Braunfels, Austin and the Hill Country.",
        },
        {
          q: "Can you fabricate custom welded frames from my drawings or sketches?",
          a: "Both work. Send a sketch, CAD file or even a phone photo with rough dimensions and we'll spec the build. For larger commercial fabrication projects we coordinate with engineers and provide stamped drawings when required.",
        },
        {
          q: "Do you do one-off welding prototypes and small-batch custom fabrication?",
          a: "Yes — prototypes, weld repairs and small production runs are everyday work for our Seguin, TX welding shop. We turn most one-off custom fabrication jobs in 1–3 weeks depending on material and complexity.",
        },
        {
          q: "Where is your welding & fabrication shop located, and how do I get a quote?",
          a: "We weld and fabricate in Seguin, TX and serve Central Texas, the Hill Country, San Antonio, Austin and the Texas Gulf Coast. Send sketches and photos through our quote form or call +1 (830) 390-9588 for a same-business-day response on your custom fabrication project.",
        },
      ]}
      applications={["Sail Shade Posts", "Custom Awning Frames", "Ranch Equipment", "Marine Hardware", "Brackets & Mounts", "Prototypes & Repair"]}
      cityCallouts={[
        {
          to: "/seguin",
          city: "Seguin",
          anchorText: "Welding & Fabrication Shop in Seguin, TX",
          blurb: "Our home shop. Walk-in consults, drop-off repairs, and pickup welcome — full TIG/MIG and canvas line on-site.",
        },
        {
          to: "/new-braunfels",
          city: "New Braunfels",
          anchorText: "Custom Welding for New Braunfels & Comal County",
          blurb: "Aluminum awning frames, steel patio supports, and custom brackets — short drive from Seguin for on-site measurement.",
        },
        {
          to: "/san-antonio",
          city: "San Antonio",
          anchorText: "Aluminum & Steel Fabrication for San Antonio",
          blurb: "Commercial sail-shade posts, storefront awning frames, and one-off welded structures across Bexar County.",
        },
        {
          to: "/austin",
          city: "Austin",
          anchorText: "Welding & Canvas Fabrication for Austin, TX",
          blurb: "Custom welded frames and Sunbrella canvas builds delivered into Austin and the I-35 corridor.",
        },
        {
          to: "/boerne",
          city: "Boerne",
          anchorText: "Hill Country Welding & Custom Builds",
          blurb: "Ranch gates, equipment covers, and patio awning frames for Boerne and the surrounding Hill Country.",
        },
        {
          to: "/fredericksburg",
          city: "Fredericksburg",
          anchorText: "Custom Fabrication for Fredericksburg & Gillespie County",
          blurb: "Winery patio frames, sail-shade posts, and bespoke welded projects across the Hill Country.",
        },
        {
          to: "/kerrville",
          city: "Kerrville",
          anchorText: "Welding & Canvas Work for Kerrville, TX",
          blurb: "Aluminum frames, steel structures, and replacement canvas for Kerr County properties.",
        },
        {
          to: "/corpus-christi",
          city: "Corpus Christi",
          anchorText: "Marine Welding & Canvas for the Coastal Bend",
          blurb: "TIG-welded aluminum brackets, T-top frames, and marine-grade canvas patterned at the dock.",
        },
        {
          to: "/houston",
          city: "Houston",
          anchorText: "Custom Welding & Fabrication for Greater Houston",
          blurb: "Commercial awning frames, sail-shade posts, and custom welded brackets delivered into Greater Houston.",
        },
      ]}
      gallerySectionPlacement="beforeLetsTalk"
      gallerySection={{
        eyebrow: "Built In Texas · See Our Work",
        title: "Custom Welding & Fabrication — Recent Shop Projects",
        intro: (
          <div className="text-base text-navy-deep/80 md:text-lg">
            <figure className="mb-4 ml-0 w-full overflow-hidden border-2 border-navy-deep/15 shadow-card sm:float-right sm:ml-6 sm:mb-3 sm:w-[44%] md:w-[38%] lg:w-[34%]">
              <img
                src={bleacherWeldDetail}
                alt="Close-up of a freshly welded and red powder-coated steel bracket joining a stadium-style bleacher frame to a red mesh seat back, with stack-of-dimes MIG welds visible at the gusset and stainless fasteners — fabricated by Texas Made Shade in Seguin, TX"
                loading="lazy"
                decoding="async"
                width={1356}
                height={1808}
                className="block h-auto w-full object-cover"
              />
              <figcaption className="bg-navy-deep px-4 py-3 font-display text-xs uppercase tracking-[0.2em] text-white">
                MIG-welded bracket · red powder-coat finish
              </figcaption>
            </figure>
            <p>
              Every job leaves our Seguin shop built to last. From custom-welded aluminum awning frames to heavy-duty steel sail-shade posts, marine canvas, and one-off ranch builds — this is what "Texas Made" looks like up close. Browse a few recent projects below, then tell us what you need built.
            </p>
            <p className="mt-4">
              Up close, the difference is in the details — full-penetration MIG welds at every gusset, structural steel cleaned and primed before color, and a baked-on powder-coat finish that handles Texas sun, rain, and salt air without flaking. Whether it's a single bracket or a full bleacher frame, you get the same shop standard on every weld we strike.
            </p>
            <div className="clear-both" />
          </div>
        ),
        subsections: [
          {
            heading: "Aluminum & Steel Welding for South Texas & the Hill Country",
            body: (
              <>
                We deliver TIG and MIG welded frames across <Link to="/san-antonio" className="text-red underline">San Antonio</Link>,{" "}
                <Link to="/new-braunfels" className="text-red underline">New Braunfels</Link>,{" "}
                <Link to="/seguin" className="text-red underline">Seguin</Link>,{" "}
                <Link to="/boerne" className="text-red underline">Boerne</Link>, and{" "}
                <Link to="/fredericksburg" className="text-red underline">Fredericksburg</Link>. Coastal-grade aluminum, structural steel, and powder-coat finishing — all built in our Seguin shop.
              </>
            ),
          },
          {
            heading: "Custom Canvas Fabrication for Austin & the Texas Gulf Coast",
            body: (
              <>
                Our industrial sewing line patterns and stitches Sunbrella, Tempotest, and marine vinyl for customers in{" "}
                <Link to="/austin" className="text-red underline">Austin</Link>,{" "}
                <Link to="/houston" className="text-red underline">Houston</Link>,{" "}
                <Link to="/corpus-christi" className="text-red underline">Corpus Christi</Link>, and across the Texas coast. Pair custom canvas with welded aluminum frames for a true one-shop build.
              </>
            ),
          },
        ],
        columns: 3,
        images: [
          {
            src: aluminumFrame,
            alt: "TIG-welded 6061-T6 aluminum awning frame on the fabrication bench at the Texas Made Shade Seguin shop, showing clean stack-of-dimes welds at the corner gussets",
            label: "TIG-Welded Aluminum Frame · Bench Build",
            title: "Custom aluminum awning frame, hand-welded in Seguin",
          },
          {
            src: framesYard,
            alt: "Row of finished aluminum awning frames staged in the Texas Made Shade shop yard, ready for canvas patterning and powder coat",
            label: "Production Run · Awning Frame Yard",
            title: "Multiple aluminum frames staged for canvas + finishing",
          },
          {
            src: aluminumCanopy,
            alt: "Large welded aluminum commercial canopy frame and canvas roof installed at a corporate campus in Austin, Texas — fabricated and installed by Texas Made Shade",
            label: "Commercial Canopy · Welded Aluminum Frame",
            title: "Welded aluminum commercial canopy with custom canvas roof",
          },
          {
            src: aluminumCanopyFrame,
            alt: "Aerial view of a custom-welded aluminum canopy frame with tensioned canvas panels over a corporate patio in Texas, showing TIG-welded trusses and gussets",
            label: "Aluminum Canopy Frame · Tensioned Canvas",
            title: "TIG-welded aluminum trusses supporting a tensioned canvas patio canopy",
          },
          {
            src: poolGateShade,
            alt: "Close-up of a welded aluminum gate with expanded metal mesh panels and welded tube bars fabricated by Texas Made Shade, framing a tensioned white shade sail over a commercial pool deck",
            label: "Welded Aluminum Pool Gate · Tensioned Shade Sail",
            title: "Welded aluminum gate, mesh panels, and tensioned shade sail over a commercial pool",
          },
        ],
        relatedLinks: [
          { to: "/fabric-awnings", anchor: "Custom Fabric Awnings", blurb: "Welded aluminum frames + custom canvas." },
          { to: "/metal-awnings", anchor: "Metal Awnings", blurb: "Welded steel and aluminum awning structures." },
          { to: "/sail-shades", anchor: "Sail Shades & Tension Structures", blurb: "Heavy steel posts and stitched sail panels." },
          { to: "/covers-tops", anchor: "Marine Canvas & Boat Covers", blurb: "Patterned and sewn on our industrial line." },
          { to: "/boat-covers", anchor: "Boat Covers & Bimini Tops", blurb: "TIG-welded aluminum frames + marine canvas." },
          { to: "/trailer-covers", anchor: "Ag Tarps & Stock Trailer Covers", blurb: "Custom frames and replacement tarps." },
          { to: "/pergola-covers", anchor: "Patio Covers", blurb: "Welded frames built for Texas patios." },
          { to: "/commercial-awnings", anchor: "Commercial Awnings", blurb: "Spec-built welded structures for businesses." },
          { to: "/seguin", anchor: "Seguin, TX Shop", blurb: "Visit our Seguin fabrication facility." },
        ],
      }}
      testimonials={{
        eyebrow: "Customer Stories",
        title: "What Texas Customers Say About Our Shop",
        items: [
          {
            quote: "The aluminum awning frame they welded for our patio is rock solid. Clean welds, perfect fit on-site, and the canvas was crisp. Best fab work we've had done in Seguin.",
            author: "Marcus R.",
            role: "Homeowner",
            location: "Seguin, TX",
            rating: 5,
          },
          {
            quote: "We needed a custom steel sail-shade post set engineered for a commercial property. Texas Made Shade handled drawings, welding, galvanizing, and install — all in-house. Job came back better than spec.",
            author: "Lauren P.",
            role: "Property Manager",
            location: "San Antonio, TX",
            rating: 5,
          },
          {
            quote: "Patterned my T-top at the dock, welded the aluminum brackets, and sewed the canvas. One shop, one crew, one invoice. Couldn't be happier with the boat cover.",
            author: "Captain Dave H.",
            role: "Offshore Charter",
            location: "Port Aransas, TX",
            rating: 5,
          },
        ],
      }}
      projectHighlights={{
        eyebrow: "Project Spec Sheets",
        title: "Typical Build Specs — What Goes Into Each Job",
        intro: "Materials, methods, and tolerances behind common builds that come through our Seguin shop — every project patterned, welded, and finished in-house.",
        items: [
          {
            title: "Custom Aluminum Awning Frame",
            description: "TIG-welded structural aluminum frame patterned to a residential patio for a fabric awning install.",
            specs: [
              "6061-T6 aluminum tubing, TIG welded",
              "Powder-coat finish in custom color",
              "Coastal-grade hardware",
              "Patterned on-site for exact fit",
              "Lifetime craftsmanship warranty",
            ],
          },
          {
            title: "Steel Sail-Shade Post Set",
            description: "Heavy-gauge steel posts and footings for a four-point commercial sail-shade tension structure. Pergola covers and welded aluminum frames built in Seguin, TX.",
            specs: [
              "Schedule-40 structural steel posts",
              "MIG welded base plates & gussets",
              "Engineered for 90+ mph wind load",
              "Hot-dip galvanized + powder coat",
              "Anchor bolt template included",
            ],
          },
          {
            title: "Marine Canvas + Hardware Build",
            description: "Custom-patterned T-top canvas with welded aluminum mounting brackets for an offshore center console.",
            specs: [
              "Sunbrella Marine-grade fabric",
              "TIG-welded aluminum brackets",
              "316 stainless snaps & YKK marine zips",
              "Patterned at the dock",
              "UV-stabilized marine thread",
            ],
          },
          {
            title: "Ranch Equipment Cover & Frame",
            description: "Replacement 18oz vinyl tarp with a custom-welded steel sub-frame for a working stock trailer.",
            specs: [
              "18oz vinyl-coated polyester tarp",
              "MIG-welded steel sub-frame",
              "Reinforced corner D-rings",
              "Roll-up side panels",
              "Built to ranch-duty spec",
            ],
          },
          {
            title: "One-Off Bracket & Prototype Run",
            description: "Small-batch prototype brackets and mounting hardware fabricated to a customer drawing.",
            specs: [
              "Aluminum or steel — your spec",
              "CAD or sketch accepted",
              "Quick-turn shop scheduling",
              "Powder coat or raw finish",
              "Repeat production available",
            ],
          },
        ],
      }}
    >
      <section className="border-t-4 border-navy-deep bg-cream py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ In the Shop · Seguin, TX</div>
          <h2 className="mt-2 font-display text-3xl font-bold text-navy-deep md:text-4xl">
            From Raw Aluminum to Finished Frame — Built by Hand in Seguin
          </h2>
          <p className="mt-4 max-w-3xl text-lg text-navy-deep/80">
            Every welded frame that leaves our Seguin shop is patterned, cut, fitted, and welded by the same crew that
            installs it. No outsourced sub-assemblies, no prefab kits — just clean structural aluminum, square corners,
            and welds that hold up to Texas wind, rain, and salt air.
          </p>

          <div className="mt-12 grid gap-10 lg:grid-cols-[7fr_5fr] lg:items-center">
            <figure className="overflow-hidden border-4 border-navy-deep shadow-card">
              <img
                src={shopFrameStand}
                alt="Texas Made Shade fabricator standing a fully welded structural aluminum awning frame upright against the Seguin shop wall, showing the full lattice of vertical members, diagonal braces, and horizontal rails after final TIG welding"
                width={1408}
                height={1879}
                className="block h-auto w-full object-cover"
                loading="lazy"
                decoding="async"
              />
              <figcaption className="bg-navy-deep px-4 py-3 font-display text-xs uppercase tracking-[0.2em] text-white">
                Finished aluminum frame · stood up against the shop wall
              </figcaption>
            </figure>
            <div className="lg:pt-2">
              <h3 className="font-display text-2xl font-bold text-navy-deep md:text-3xl">
                Standing Up the Finished Frame
              </h3>
              <p className="mt-4 text-lg text-navy-deep/80">
                After every joint is TIG-welded and dressed, the frame goes vertical for a final fit-check against the
                shop wall. We sight every diagonal, square every corner, and verify the bay spacing matches the canvas
                or panel pattern before the build leaves the yard.
              </p>
              <ul className="mt-6 grid gap-3 text-base text-navy-deep/85">
                <li className="flex items-start gap-2">
                  <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 bg-red" />
                  Structural aluminum tube — coastal-grade for Texas weather
                </li>
                <li className="flex items-start gap-2">
                  <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 bg-red" />
                  Diagonal bracing engineered for shear &amp; wind load
                </li>
                <li className="flex items-start gap-2">
                  <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 bg-red" />
                  Final fit-check before powder coat &amp; canvas patterning
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-16 grid gap-10 lg:grid-cols-[5fr_7fr] lg:items-center">
            <div className="lg:order-1 lg:pt-2">
              <h3 className="font-display text-2xl font-bold text-navy-deep md:text-3xl">
                The Hands Behind the Welds
              </h3>
              <p className="mt-4 text-lg text-navy-deep/80">
                Same crew that quotes the job welds the job. When you call our Seguin shop you talk to the fabricator
                who&apos;ll lay the bead — not a sales desk on the other side of the country. Welding gloves on, hood
                down, fit-up checked twice before a single arc gets struck.
              </p>
              <ul className="mt-6 grid gap-3 text-base text-navy-deep/85">
                <li className="flex items-start gap-2">
                  <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 bg-red" />
                  Hand-fit, hand-welded — every joint inspected
                </li>
                <li className="flex items-start gap-2">
                  <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 bg-red" />
                  TIG on aluminum, MIG on heavy steel
                </li>
                <li className="flex items-start gap-2">
                  <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 bg-red" />
                  One shop, one crew, one invoice
                </li>
              </ul>
              <p className="mt-6 text-base text-navy-deep/75">
                Send us a sketch, a photo, or rough dimensions — we&apos;ll reply same business day with a quote and a
                build plan.
              </p>
            </div>
            <figure className="overflow-hidden border-4 border-navy-deep shadow-card lg:order-2">
              <img
                src={fabricatorPortrait}
                alt="Close-up of a Texas Made Shade fabricator in welding gloves and safety glasses standing inside a freshly built aluminum awning frame at the Seguin, TX shop, gripping the vertical tubing during a final fit-check"
                width={1408}
                height={1879}
                className="block h-auto w-full object-cover"
                loading="lazy"
                decoding="async"
              />
              <figcaption className="bg-navy-deep px-4 py-3 font-display text-xs uppercase tracking-[0.2em] text-white">
                Same crew that quotes the job welds the job
              </figcaption>
            </figure>
          </div>
        </div>
      </section>
    </ServicePage>
  ),
});
